import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { TakebacksTable } from "../components/TakebacksTable";
import { useTenant } from "../lib/tenant-context";
import { tenantFetch } from "../lib/api-client";

interface Program {
  id: string;
  name: string;
  type: "takeback" | "trade_in" | "resale";
  status: string;
  config: string | Record<string, unknown>;
  external_id: string | null;
}

export const Route = createFileRoute("/programs_/$id_/takebacks")({
  component: ProgramTakebacks,
});

// Mock takeback records for demo
const MOCK_TAKEBACKS = [
  {
    id: "tb-mc-001",
    product: "Ultraboost 22",
    sku: "GX5460",
    status: "completed",
    value: 45.0,
    condition: "Good",
    customer: "James K.",
    date: "2026-02-19",
  },
  {
    id: "tb-mc-002",
    product: "Stan Smith",
    sku: "FX5502",
    status: "pending",
    value: 28.0,
    condition: "Fair",
    customer: "Sarah M.",
    date: "2026-02-19",
  },
  {
    id: "tb-mc-003",
    product: "NMD_R1",
    sku: "GW9462",
    status: "shipped",
    value: 35.0,
    condition: "Good",
    customer: "Chen R.",
    date: "2026-02-18",
  },
  {
    id: "tb-mc-004",
    product: "Forum Low",
    sku: "FY7756",
    status: "completed",
    value: 42.0,
    condition: "Excellent",
    customer: "Maria L.",
    date: "2026-02-18",
  },
  {
    id: "tb-mc-005",
    product: "Samba OG",
    sku: "B75806",
    status: "graded",
    value: 38.0,
    condition: "Good",
    customer: "Tom W.",
    date: "2026-02-18",
  },
  {
    id: "tb-mc-006",
    product: "Gazelle",
    sku: "BB5476",
    status: "completed",
    value: 32.0,
    condition: "Fair",
    customer: "Alex P.",
    date: "2026-02-17",
  },
  {
    id: "tb-mc-007",
    product: "Superstar",
    sku: "EG4958",
    status: "pending",
    value: 25.0,
    condition: "Good",
    customer: "Emily D.",
    date: "2026-02-17",
  },
  {
    id: "tb-mc-008",
    product: "ZX 8000",
    sku: "FW4910",
    status: "completed",
    value: 55.0,
    condition: "Excellent",
    customer: "Jordan B.",
    date: "2026-02-17",
  },
  {
    id: "tb-mc-009",
    product: "Continental 80",
    sku: "EE5342",
    status: "shipped",
    value: 30.0,
    condition: "Good",
    customer: "David H.",
    date: "2026-02-16",
  },
  {
    id: "tb-mc-010",
    product: "Handball Spezial",
    sku: "DB3021",
    status: "completed",
    value: 48.0,
    condition: "Excellent",
    customer: "Lisa N.",
    date: "2026-02-16",
  },
  {
    id: "tb-mc-011",
    product: "Campus 80s",
    sku: "FZ6154",
    status: "graded",
    value: 36.0,
    condition: "Fair",
    customer: "Pat C.",
    date: "2026-02-16",
  },
  {
    id: "tb-mc-012",
    product: "Ozweego",
    sku: "EE7002",
    status: "completed",
    value: 40.0,
    condition: "Good",
    customer: "Robin K.",
    date: "2026-02-15",
  },
  {
    id: "tb-mc-013",
    product: "Nite Jogger",
    sku: "EF5404",
    status: "pending",
    value: 33.0,
    condition: "Good",
    customer: "Casey M.",
    date: "2026-02-15",
  },
  {
    id: "tb-mc-014",
    product: "Rivalry Low",
    sku: "EE4657",
    status: "completed",
    value: 29.0,
    condition: "Fair",
    customer: "Riley S.",
    date: "2026-02-15",
  },
  {
    id: "tb-mc-015",
    product: "Adilette",
    sku: "EG4919",
    status: "shipped",
    value: 15.0,
    condition: "Good",
    customer: "Drew F.",
    date: "2026-02-14",
  },
  {
    id: "tb-mc-016",
    product: "Ultraboost 21",
    sku: "FY0378",
    status: "completed",
    value: 52.0,
    condition: "Excellent",
    customer: "Jamie T.",
    date: "2026-02-14",
  },
  {
    id: "tb-mc-017",
    product: "Yeezy 350 V2",
    sku: "FW3042",
    status: "graded",
    value: 85.0,
    condition: "Good",
    customer: "Sam G.",
    date: "2026-02-14",
  },
  {
    id: "tb-mc-018",
    product: "NMD_R1 V2",
    sku: "FW6410",
    status: "completed",
    value: 38.0,
    condition: "Good",
    customer: "Quinn H.",
    date: "2026-02-13",
  },
  {
    id: "tb-mc-019",
    product: "Terrex Free Hiker",
    sku: "EF6588",
    status: "pending",
    value: 62.0,
    condition: "Excellent",
    customer: "Avery L.",
    date: "2026-02-13",
  },
  {
    id: "tb-mc-020",
    product: "Stan Smith Lux",
    sku: "HP2201",
    status: "completed",
    value: 44.0,
    condition: "Good",
    customer: "Skyler R.",
    date: "2026-02-13",
  },
];

function DemoBadge() {
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full"
      style={{ backgroundColor: "rgba(0,251,169,0.15)", color: "#171717" }}
    >
      Demo Data
    </span>
  );
}

function getStatusColor(status: string): string {
  const colors: Record<string, string> = {
    completed: "#00FBA9",
    processed: "#00FBA9",
    shipped: "#4A90D9",
    in_transit: "#4A90D9",
    delivered: "#7B61FF",
    graded: "#F5A623",
    pending: "#A9A9A9",
    created: "#A9A9A9",
    unknown: "#A9A9A9",
  };
  return colors[status.toLowerCase()] || "#A9A9A9";
}

function ProgramTakebacks() {
  const params = Route.useParams() as { id: string };
  const id = params.id;
  const { tenantId } = useTenant();
  const [program, setProgram] = useState<Program | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    setLoading(true);
    setError("");
    fetchProgram();
  }, [id, tenantId]);

  const fetchProgram = async () => {
    try {
      const response = await tenantFetch(`/api/programs/${id}`);
      if (response.ok) {
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          const data = await response.json();
          setProgram(data);
          return;
        }
      }
      const demoProgram = getDemoProgram(tenantId, id);
      if (demoProgram) {
        setProgram(demoProgram);
      } else {
        setError("Program not found");
      }
    } catch {
      const demoProgram = getDemoProgram(tenantId, id);
      if (demoProgram) {
        setProgram(demoProgram);
      } else {
        setError("Failed to load program");
      }
    } finally {
      setLoading(false);
    }
  };

  function getDemoProgram(tid: string, pid: string): Program | null {
    if (tid === "arcteryx") {
      const programs: Record<string, Program> = {
        "prg-arc-1": {
          id: "prg-arc-1",
          name: "Arc'teryx ReBIRD",
          type: "trade_in",
          status: "active",
          config: { tes_client_id: "arcteryx-rebird-prod" },
          external_id: "arcteryx-rebird-prod",
        },
        "prg-arc-2": {
          id: "prg-arc-2",
          name: "Arc'teryx Repair Program",
          type: "takeback",
          status: "draft",
          config: {},
          external_id: null,
        },
      };
      return programs[pid] || null;
    }
    const defaultPrograms: Record<string, Program> = {
      "prg-lulu-1": {
        id: "prg-lulu-1",
        name: "Like New Trade-In",
        type: "trade_in",
        status: "active",
        config: { tes_client_id: "lululemon-likenew-prod" },
        external_id: "lululemon-likenew-prod",
      },
    };
    return defaultPrograms[pid] || null;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div
          className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin"
          style={{ borderColor: "#00FBA9", borderTopColor: "transparent" }}
        />
      </div>
    );
  }

  if (error || !program) {
    return (
      <div className="text-center py-12">
        <h1 className="text-xl font-light mb-2" style={{ color: "#171717" }}>
          {error || "Program not found"}
        </h1>
        <Link to="/programs" className="text-sm" style={{ color: "#00FBA9" }}>
          Back to Programs
        </Link>
      </div>
    );
  }

  const config =
    typeof program.config === "string"
      ? JSON.parse(program.config)
      : program.config || {};
  const isConfigured = !!(program.external_id || config.tes_client_id);

  const programWithParsedConfig = {
    ...program,
    config:
      typeof program.config === "string"
        ? JSON.parse(program.config)
        : program.config,
  };

  return (
    <div>
      {/* Breadcrumb */}
      <div
        className="flex items-center gap-2 text-sm mb-6"
        style={{ color: "#A9A9A9" }}
      >
        <Link to="/programs" className="hover:underline">
          Programs
        </Link>
        <span>/</span>
        <Link
          to="/programs/$id"
          params={{ id: program.id }}
          className="hover:underline"
        >
          {program.name}
        </Link>
        <span>/</span>
        <span style={{ color: "#171717" }}>Takebacks</span>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <div
            className="w-12 h-12  flex items-center justify-center"
            style={{ backgroundColor: "#00FBA9" }}
          >
            <TakebackIcon />
          </div>
          <div>
            <h1 className="text-xl font-light" style={{ color: "#171717" }}>
              {program.name}
            </h1>
            <p className="text-sm" style={{ color: "#A9A9A9" }}>
              Takeback Records
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      {!isConfigured ? (
        <MockTakebacksTable />
      ) : (
        <TakebacksTable program={programWithParsedConfig} />
      )}
    </div>
  );
}

// Mock table displayed when TES is not configured
function MockTakebacksTable() {
  const [search, setSearch] = useState("");
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const filtered = search
    ? MOCK_TAKEBACKS.filter(
        (t) =>
          t.id.toLowerCase().includes(search.toLowerCase()) ||
          t.product.toLowerCase().includes(search.toLowerCase()) ||
          t.customer.toLowerCase().includes(search.toLowerCase()),
      )
    : MOCK_TAKEBACKS;

  const totalFees = MOCK_TAKEBACKS.reduce((sum, t) => sum + t.value * 0.03, 0);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-medium" style={{ color: "#171717" }}>
            Takebacks
          </h2>
          <DemoBadge />
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <SearchIcon />
            <input
              type="text"
              placeholder="Search by ID, product, or customer..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 pr-4 py-2 text-sm border rounded-lg w-64"
              style={{
                borderColor: "rgba(169,169,169,0.3)",
                backgroundColor: "#FFFFFF",
              }}
            />
          </div>
          <span className="text-sm" style={{ color: "#A9A9A9" }}>
            {MOCK_TAKEBACKS.length} total
          </span>
          <span className="text-sm font-medium" style={{ color: "#00FBA9" }}>
            Fees: ${totalFees.toFixed(2)}
          </span>
        </div>
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr
                style={{
                  backgroundColor: "#FAFAFA",
                  borderBottom: "1px solid rgba(169,169,169,0.2)",
                }}
              >
                <th
                  className="text-left px-4 py-3 font-medium"
                  style={{ color: "#A9A9A9" }}
                >
                  ID
                </th>
                <th
                  className="text-left px-4 py-3 font-medium"
                  style={{ color: "#A9A9A9" }}
                >
                  Product
                </th>
                <th
                  className="text-left px-4 py-3 font-medium"
                  style={{ color: "#A9A9A9" }}
                >
                  Customer
                </th>
                <th
                  className="text-left px-4 py-3 font-medium"
                  style={{ color: "#A9A9A9" }}
                >
                  Value
                </th>
                <th
                  className="text-left px-4 py-3 font-medium"
                  style={{ color: "#00FBA9" }}
                >
                  Transaction Fee
                </th>
                <th
                  className="text-left px-4 py-3 font-medium"
                  style={{ color: "#A9A9A9" }}
                >
                  Status
                </th>
                <th
                  className="text-left px-4 py-3 font-medium"
                  style={{ color: "#A9A9A9" }}
                >
                  Date
                </th>
                <th
                  className="text-right px-4 py-3 font-medium"
                  style={{ color: "#A9A9A9" }}
                ></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((tb) => (
                <>
                  <tr
                    key={tb.id}
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                    style={{ borderBottom: "1px solid rgba(169,169,169,0.1)" }}
                    onClick={() =>
                      setExpandedId(expandedId === tb.id ? null : tb.id)
                    }
                  >
                    <td
                      className="px-4 py-3 font-mono text-xs"
                      style={{ color: "#171717" }}
                    >
                      {tb.id}
                    </td>
                    <td className="px-4 py-3" style={{ color: "#171717" }}>
                      <div>
                        <p>{tb.product}</p>
                        <p className="text-xs" style={{ color: "#A9A9A9" }}>
                          {tb.sku}
                        </p>
                      </div>
                    </td>
                    <td className="px-4 py-3" style={{ color: "#171717" }}>
                      {tb.customer}
                    </td>
                    <td
                      className="px-4 py-3 font-medium"
                      style={{ color: "#171717" }}
                    >
                      ${tb.value.toFixed(2)}
                    </td>
                    <td
                      className="px-4 py-3 font-medium"
                      style={{ color: "#00FBA9" }}
                    >
                      ${(tb.value * 0.03).toFixed(2)}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className="px-2 py-0.5 rounded-full text-xs capitalize"
                        style={{
                          backgroundColor: `${getStatusColor(tb.status)}20`,
                          color: getStatusColor(tb.status),
                        }}
                      >
                        {tb.status}
                      </span>
                    </td>
                    <td className="px-4 py-3" style={{ color: "#A9A9A9" }}>
                      {tb.date}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <ChevronIcon expanded={expandedId === tb.id} />
                    </td>
                  </tr>
                  {expandedId === tb.id && (
                    <tr
                      key={`${tb.id}-detail`}
                      style={{ backgroundColor: "#FAFAFA" }}
                    >
                      <td colSpan={8} className="px-4 py-4">
                        <div className="grid grid-cols-4 gap-6">
                          <div>
                            <h4
                              className="text-xs font-medium mb-2 uppercase tracking-wide"
                              style={{ color: "#A9A9A9" }}
                            >
                              Trade-In Details
                            </h4>
                            <div className="space-y-2 text-sm">
                              <p>
                                <span style={{ color: "#A9A9A9" }}>
                                  Reference:
                                </span>{" "}
                                <span className="font-mono text-xs">
                                  {tb.id}
                                </span>
                              </p>
                              <p>
                                <span style={{ color: "#A9A9A9" }}>
                                  Product:
                                </span>{" "}
                                {tb.product}
                              </p>
                              <p>
                                <span style={{ color: "#A9A9A9" }}>SKU:</span>{" "}
                                {tb.sku}
                              </p>
                              <p>
                                <span style={{ color: "#A9A9A9" }}>
                                  Condition:
                                </span>{" "}
                                {tb.condition}
                              </p>
                            </div>
                          </div>
                          <div>
                            <h4
                              className="text-xs font-medium mb-2 uppercase tracking-wide"
                              style={{ color: "#A9A9A9" }}
                            >
                              Customer
                            </h4>
                            <div className="space-y-2 text-sm">
                              <p>
                                <span style={{ color: "#A9A9A9" }}>Name:</span>{" "}
                                {tb.customer}
                              </p>
                              <p>
                                <span style={{ color: "#A9A9A9" }}>Email:</span>{" "}
                                {tb.customer.split(" ")[0].toLowerCase()}
                                @example.com
                              </p>
                            </div>
                          </div>
                          <div>
                            <h4
                              className="text-xs font-medium mb-2 uppercase tracking-wide"
                              style={{ color: "#A9A9A9" }}
                            >
                              Offer & Payment
                            </h4>
                            <div className="space-y-2 text-sm">
                              <p>
                                <span style={{ color: "#A9A9A9" }}>
                                  Estimated Value:
                                </span>{" "}
                                <span className="font-medium">
                                  ${tb.value.toFixed(2)}
                                </span>
                              </p>
                              <p>
                                <span style={{ color: "#A9A9A9" }}>
                                  Transaction Fee (3%):
                                </span>{" "}
                                <span
                                  className="font-medium"
                                  style={{ color: "#00FBA9" }}
                                >
                                  ${(tb.value * 0.03).toFixed(2)}
                                </span>
                              </p>
                              <p>
                                <span style={{ color: "#A9A9A9" }}>
                                  Offer Type:
                                </span>{" "}
                                Store Credit
                              </p>
                            </div>
                          </div>
                          <div>
                            <h4
                              className="text-xs font-medium mb-2 uppercase tracking-wide"
                              style={{ color: "#A9A9A9" }}
                            >
                              Timeline
                            </h4>
                            <div className="space-y-2 text-sm">
                              <p>
                                <span style={{ color: "#A9A9A9" }}>
                                  Submitted:
                                </span>{" "}
                                {tb.date}
                              </p>
                              <p>
                                <span style={{ color: "#A9A9A9" }}>
                                  Status:
                                </span>{" "}
                                <span className="capitalize">{tb.status}</span>
                              </p>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// Icons
function TakebackIcon() {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#171717"
      strokeWidth="1.5"
    >
      <path
        d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2"
        strokeLinecap="round"
      />
      <path
        d="M12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22"
        strokeLinecap="round"
        strokeDasharray="3 3"
      />
      <path d="M8 12L12 8L16 12" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M12 8V16" strokeLinecap="round" />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#A9A9A9"
      strokeWidth="1.5"
      className="absolute left-3 top-1/2 -translate-y-1/2"
    >
      <circle cx="11" cy="11" r="8" />
      <path d="M21 21L16.65 16.65" strokeLinecap="round" />
    </svg>
  );
}

function ChevronIcon({ expanded }: { expanded: boolean }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#A9A9A9"
      strokeWidth="1.5"
      className="transition-transform"
      style={{ transform: expanded ? "rotate(180deg)" : "rotate(0deg)" }}
    >
      <path d="M6 9L12 15L18 9" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
